import React, { useState, useEffect } from 'react';
import { Target, Trophy, Edit3 } from 'lucide-react';
import { motion } from 'framer-motion';
import { creatorAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/utils';

export default function GoalMeter({ roomId, isCreator, externalCurrent }) {
  const [goal, setGoal] = useState({ goal_amount: 0, goal_current: 0, goal_label: '' });
  const [editing, setEditing] = useState(false);
  const [newAmount, setNewAmount] = useState('');
  const [newLabel, setNewLabel] = useState('');

  useEffect(() => {
    creatorAPI.getGoal(roomId).then(r => setGoal(r.data)).catch(() => {});
  }, [roomId]);

  // Update current when external tips come in
  useEffect(() => {
    if (externalCurrent !== undefined) {
      setGoal(prev => ({ ...prev, goal_current: externalCurrent }));
    }
  }, [externalCurrent]);

  const saveGoal = async () => {
    const amt = parseFloat(newAmount);
    if (isNaN(amt) || amt <= 0) return;
    try {
      await creatorAPI.setGoal(roomId, amt, newLabel || 'Session Goal');
      setGoal({ goal_amount: amt, goal_current: 0, goal_label: newLabel || 'Session Goal' });
      setEditing(false);
      toast.success('Goal set!');
    } catch (e) { toast.error('Failed to set goal'); }
  };

  if (!goal.goal_amount && !isCreator) return null;

  const pct = goal.goal_amount > 0 ? Math.min(100, (goal.goal_current / goal.goal_amount) * 100) : 0;
  const isComplete = pct >= 100;

  return (
    <div className="border-t border-zinc-800 bg-zinc-900/50 px-3 py-2" data-testid="goal-meter">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-bold text-amber-400 flex items-center gap-1.5">
          {isComplete ? <Trophy size={12} className="text-yellow-400" /> : <Target size={12} />}
          {goal.goal_label || 'Session Goal'}
        </span>
        {isCreator && (
          <Button onClick={() => { setEditing(!editing); setNewAmount(goal.goal_amount?.toString() || ''); setNewLabel(goal.goal_label || ''); }} variant="ghost" size="sm" className="text-zinc-400 text-xs h-5 px-1.5" data-testid="edit-goal-btn">
            <Edit3 size={10} />
          </Button>
        )}
      </div>

      {goal.goal_amount > 0 && !editing && (
        <>
          <div className="w-full bg-zinc-800 rounded-full h-3 overflow-hidden relative">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className={`h-full rounded-full ${isComplete ? 'bg-gradient-to-r from-yellow-400 to-amber-500' : 'bg-gradient-to-r from-amber-500 to-orange-500'}`}
            />
            {isComplete && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: [0.3, 0.8, 0.3] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 to-amber-500/20 rounded-full"
              />
            )}
          </div>
          <div className="flex justify-between mt-1 text-[10px]">
            <span className="text-zinc-400">{formatCurrency(goal.goal_current)}</span>
            <span className={`font-bold ${isComplete ? 'text-yellow-400' : 'text-zinc-500'}`}>
              {isComplete ? 'GOAL REACHED!' : `${formatCurrency(goal.goal_amount)} goal`}
            </span>
          </div>
        </>
      )}

      {editing && (
        <div className="flex items-center gap-1.5 mt-1">
          <Input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="Goal label" className="flex-1 h-7 bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="goal-label-input" />
          <Input type="number" value={newAmount} onChange={e => setNewAmount(e.target.value)} placeholder="$" className="w-16 h-7 bg-zinc-800 border-zinc-700 text-white text-xs" data-testid="goal-amount-input" />
          <Button onClick={saveGoal} size="sm" className="h-7 px-2 bg-amber-500 text-black text-xs font-bold rounded" data-testid="save-goal-btn">Set</Button>
          <Button onClick={() => setEditing(false)} variant="ghost" size="sm" className="h-7 px-1.5 text-zinc-500 text-xs">X</Button>
        </div>
      )}
    </div>
  );
}
